(function (global) {
  // Visualizer.Message module: builds and posts messages to the configurator iframe
  const Message = {
    _last: null,
    _lastSentAt: 0,

    // Classic payload builder (extracted)
    // @param {Object} config - Configuration object
    // @param {string} [configuratorType='classic'] - Type of configurator ('classic', 'wall-unit')
    buildClassicPayload(config, configuratorType = 'classic') {
      let color = config["color"];
      if (config["materialRange"] && (config["materialRange"].startsWith('/') || config["materialRange"].startsWith('http'))) {
        color = config["materialRange"];
      }
      if (config["materialRange"] === 'luxe') {
        color = config["luxeColor"];
      }

      const isWallUnit = configuratorType === 'wall-unit'

      const allowedBaseTypes = new Set(['plinth', 'legs', 'floor', 'wall'])
      let baseTypeType
      let mountTypeValue

      if (isWallUnit) {
        // Wall units are always wall-mounted
        mountTypeValue = 'wall'
        baseTypeType = 'wall'
      } else {
        mountTypeValue = config["mountType"]
        baseTypeType = (config["baseType-type"] || '').toString().trim().toLowerCase()
        if (config["mountType"] === 'wall') baseTypeType = 'wall'
        if (!allowedBaseTypes.has(baseTypeType)) baseTypeType = 'floor'
      }

      const baseType = { type: baseTypeType }

      const result = {
        source: 'cabinet-configurator-app',
        payload: {
          type: 'setStartConfig',
          config: {
            color: color,
            mountType: mountTypeValue,
            baseType,
            countertop: this.buildCountertopPayload(config),
            topPanelEnabled: this.buildTopPanelEnabled(config),
            dimensions: {
              width: Number(config["dimensions-width"]),
              height: Number(config["dimensions-height"]),
              depth: Number(config["dimensions-depth"]),
            },
            doorHangType: config["doors-type"],
          }
        }
      }

      // Only add legs/plinth properties for classic (not wall-unit)
      if (!isWallUnit) {
        if (result.payload.config.baseType.type === 'legs') {
          result.payload.config.baseType.legCode = config["baseType-legCode"]
          result.payload.config.baseType.color = config["baseType-color"]
        } else if (result.payload.config.baseType.type === 'plinth') {
          result.payload.config.baseType.height = Number(config["baseType-plinth-height"])
        }
      }

      let doorsColor = config["doorsColor"];
      if (config["doorsMaterialRange"] && (config["doorsMaterialRange"].startsWith('/') || config["doorsMaterialRange"].startsWith('http'))) {
        doorsColor = config["doorsMaterialRange"];
      }
      if (config["doorsMaterialRange"] === 'luxe') {
        doorsColor = config["doorsLuxeColor"];
      }
      if (config["doorsMaterialDifferent"] === 'same') {
        doorsColor = color;
      }

      if (!doorsColor) return false;

      const frontsConfig = {
        color: doorsColor,
        style: config["doors-style"] || undefined
      }
      result.payload.config.fronts = frontsConfig;
      result.payload.config.frameFaceDividerPosition = config['doorsDividerPosition'] ?? undefined;

      if (config['edge-color-yes-no'] === 'no') {
        result.payload.config.edgeColor = undefined;
      } else if (config['edge-color']) {
        result.payload.config.edgeColor = config["edge-color"]
      }

      if (config["adjustable"]) {
        result.payload.config.adjustableShelves = config["adjustable"]?.includes('true')
      }

      const handlesEnabled = (() => {
        const raw = config["handlesAdd"]
        if (raw === undefined || raw === null || raw === '') return true
        const val = String(raw).toLowerCase().trim()
        return !(val === 'off' || val === 'false' || val === '0' || val === 'no')
      })()

      if (handlesEnabled && config["handles-code"] && config["handles-color"]) {
        result.payload.config.handles = {
          code: config["handles-code"],
          color: config["handles-color"]
        }
      }

      if (config["countertopColor"] === "null" || !config["countertopColor"]) result.payload.config.countertopColor = undefined;
      else result.payload.config.countertopColor = config["countertopColor"]

      if (config["faceFrame"]) result.payload.config.withFaceFrame = config["faceFrame"]?.includes('true')
      if (config['columnsCount']) result.payload.config.columnsCount = Number(config['columnsCount'])
      const cableHolePosition = config["cable-hole-position"];
      if (config["cable-hole-position"]) result.payload.config.cableHolePosition = cableHolePosition === 'none' ? undefined : cableHolePosition;
      const skirtingCutout = this.buildSkirtingCutout(config)
      if (skirtingCutout) result.payload.config.skirtingCutout = skirtingCutout

      if (config["shelvesCount"]) result.payload.config.shelvesCount = Number(config["shelvesCount"])
      else if (Object.keys(config).filter((key) => key.includes('shelvesCount-')).length > 0) {
        result.payload.config.shelvesCount = []
        const keys = Object.keys(config).filter((key) => key.includes('shelvesCount-')).map(key => Number(key.replace(/\D/g, '')))
        let max = 1
        keys.forEach(key => { if (key > max) max = key })
        for (let i = 1; i <= max; i++) {
          result.payload.config.shelvesCount.push(Number(config[`shelvesCount-${i}`] || 0))
        }
      }

      // Apply door-specific parameters for classic cupboards as well
      try {
        // Rail/Stile configurable widths
        const cfgRail = (config["rail-width"] != null && !isNaN(Number(config["rail-width"]))) ? Number(config["rail-width"]) : undefined
        const cfgStile = (config["stile-width"] != null && !isNaN(Number(config["stile-width"]))) ? Number(config["stile-width"]) : undefined
        if (cfgRail !== undefined) result.payload.config.fronts.configurableRailWidth = cfgRail
        if (cfgStile !== undefined) result.payload.config.fronts.configurableStileWidth = cfgStile
      } catch (e) {
        console.warn('Failed to apply classic door parameters:', e)
      }

      result.payload.config.columns = this.buildColumnsPayload(config, configuratorType)

      return result
    },

    // Door payload builder (extracted)
    buildDoorPayload(config) {
      // Determine color priority: explicit doors* first, then generic materialRange/luxe/color
      let finalColor = config["doorsColor"]
      if (config["doorsMaterialRange"] && (config["doorsMaterialRange"].startsWith('/') || config["doorsMaterialRange"].startsWith('http'))) {
        finalColor = config["doorsMaterialRange"]
      }
      if (config["doorsMaterialRange"] === 'luxe') {
        finalColor = config["doorsLuxeColor"]
      }

      const width = Number(config["dimensions-width"]) || 0
      const height = Number(config["dimensions-height"]) || 0
      const openingDirection =
        config["doors-openingDirection-1"] ||
        config["doors-simpleOpeningDirection-1"] ||
        config["doors-openingDirection"] ||
        config["doors-simpleOpeningDirection"] ||
        undefined
      const style = config["doors-style"] || undefined
      const configurableRailWidth = (config["rail-width"] != null && !isNaN(Number(config["rail-width"]))) ? Number(config["rail-width"]) : undefined
      const configurableStileWidth = (config["stile-width"] != null && !isNaN(Number(config["stile-width"]))) ? Number(config["stile-width"]) : undefined

      // Optional numeric options
      let hingesCount = (config["hingesCount"] != null ? Number(config["hingesCount"]) : (config["hinges-count"] != null ? Number(config["hinges-count"]) : 2))
      const allowedHinges = new Set([0, 2, 3, 4])
      if (isNaN(hingesCount) || !allowedHinges.has(hingesCount)) hingesCount = 2
      // Hinge offsets: default to 30 if absent/invalid, clamp minimum to 30
      let topHingeOffset = (config["top-hinge-offset"] != null ? Number(config["top-hinge-offset"]) : (config["topHingeOffset"] != null ? Number(config["topHingeOffset"]) : undefined))
      let bottomHingeOffset = (config["bottom-hinge-offset"] != null ? Number(config["bottom-hinge-offset"]) : (config["bottomHingeOffset"] != null ? Number(config["bottomHingeOffset"]) : undefined))
      topHingeOffset = Number.isFinite(topHingeOffset) ? topHingeOffset : 30
      bottomHingeOffset = Number.isFinite(bottomHingeOffset) ? bottomHingeOffset : 30
      if (bottomHingeOffset <= 0) bottomHingeOffset = 30;
      if (topHingeOffset <= 0) topHingeOffset = 30;

      const result = {
        source: 'cabinet-configurator-app',
        payload: {
          type: 'setStartConfig',
          config: {
            style: style,
            dimensions: { width, height },
            color: finalColor,
            edgeColor: undefined,
            openingDirection: openingDirection,
            hingesCount: (isNaN(hingesCount) ? 2 : hingesCount),
            columns: [],
            // optional fields below will be conditionally attached
          },
          // reFitCamera: true // can be enabled if needed
        }
      }

      // Optional new door params: configurableRailWidth and configurableStileWidth
      const supportsConfigurableBorder = config?.__supportsConfigurableBorder
      const stileRange = config?.__configurableStileWidthRange
      const railRange = config?.__configurableRailWidthRange
      const clampToRange = (val, range) => {
        if (!range) return val
        const v = Number(val)
        if (!Number.isFinite(v)) return val
        const min = Number(range.min)
        const max = Number(range.max)
        if (Number.isFinite(min) && v < min) return min
        if (Number.isFinite(max) && v > max) return max
        return v
      }
      const canApplyConfigurableBorder = supportsConfigurableBorder !== false
      if (canApplyConfigurableBorder && configurableRailWidth !== undefined && !isNaN(configurableRailWidth)) {
        const clampedRail = (supportsConfigurableBorder === true && railRange)
          ? clampToRange(configurableRailWidth, railRange)
          : configurableRailWidth
        if (Number.isFinite(Number(clampedRail))) result.payload.config.configurableRailWidth = Number(clampedRail)
      }
      if (canApplyConfigurableBorder && configurableStileWidth !== undefined && !isNaN(configurableStileWidth)) {
        const clampedStile = (supportsConfigurableBorder === true && stileRange)
          ? clampToRange(configurableStileWidth, stileRange)
          : configurableStileWidth
        if (Number.isFinite(Number(clampedStile))) result.payload.config.configurableStileWidth = Number(clampedStile)
      }
      result.payload.config.topHingeOffset = topHingeOffset
      result.payload.config.bottomHingeOffset = bottomHingeOffset

      // Edge color (same as current version: top-level edgeColor)
      if (config['edge-color-yes-no'] === 'no') {
        result.payload.config.edgeColor = undefined
      } else if (config['edge-color']) {
        result.payload.config.edgeColor = config["edge-color"]
      }
      return result
    },

    // @param {Object} config - Configuration object
    // @param {string} [configuratorType='bookcases'] - Type of configurator ('bookcases')
    buildBookcasesPayload(config, configuratorType = 'bookcases') {
      let color = config["color"]
      if (config["materialRange"] && (config["materialRange"].startsWith('/') || config["materialRange"].startsWith('http'))) {
        color = config["materialRange"]
      }
      if (config["materialRange"] === 'luxe') {
        color = config["luxeColor"]
      }

      const allowedBaseTypes = new Set(['plinth', 'legs', 'floor', 'wall'])
      let baseTypeType = (config["baseType-type"] || '').toString().trim().toLowerCase()
      if (config["mountType"] === 'wall') baseTypeType = 'wall'
      if (!allowedBaseTypes.has(baseTypeType)) baseTypeType = 'floor'
      const baseType = { type: baseTypeType }

      const result = {
        source: 'cabinet-configurator-app',
        payload: {
          type: 'setStartConfig',
          doorToggleButtonVisible: false, // specific for bookcases (no doors)
          config: {
            color: color,
            mountType: config["mountType"],
            baseType,
            columns: this.buildColumnsPayload(config, configuratorType),
            countertop: this.buildCountertopPayload(config),
            topPanelEnabled: this.buildTopPanelEnabled(config),
            dimensions: {
              width: Number(config["dimensions-width"]),
              height: Number(config["dimensions-height"]),
              depth: Number(config["dimensions-depth"]),
            },
            doorHangType: 'standard',
            faceFrameCoversVerticalDividers: true, // specific for bookcases
          }
        }
      }

      if (result.payload.config.baseType.type === 'legs') {
        result.payload.config.baseType.legCode = config["baseType-legCode"]
        result.payload.config.baseType.color = config["baseType-color"]
      } else if (result.payload.config.baseType.type === 'plinth') {
        result.payload.config.baseType.height = Number(config["baseType-plinth-height"])
      }

      // No doors structure in bookcases

      if (config['edge-color-yes-no'] === 'no') {
        result.payload.config.edgeColor = undefined
      } else if (config['edge-color']) {
        result.payload.config.edgeColor = config["edge-color"]
      }

      if (config["adjustable"]) {
        result.payload.config.adjustableShelves = config["adjustable"]?.includes('true')
      }

      if (config["shelvesCount"]) {
        result.payload.config.shelvesCount = Number(config["shelvesCount"])
      } else if (Object.keys(config).filter((key) => key.includes('shelvesCount-')).length > 0) {
        result.payload.config.shelvesCount = []
        const keys = Object.keys(config).filter((key) => key.includes('shelvesCount-')).map(key => Number(key.replace(/\D/g, '')))
        let max = 1
        keys.forEach(key => { if (key > max) max = key })
        for (let i = 1; i <= max; i++) {
          result.payload.config.shelvesCount.push(Number(config[`shelvesCount-${i}`] || 0))
        }
      }

      // No handles for bookcases

      if (config["countertopColor"] === "null" || !config["countertopColor"]) result.payload.config.countertopColor = undefined
      else result.payload.config.countertopColor = config["countertopColor"]

      if (config["faceFrame"]) result.payload.config.withFaceFrame = config["faceFrame"]?.includes('true')
      if (config['columnsCount']) result.payload.config.columnsCount = Number(config['columnsCount'])
      const cableHolePosition = config["cable-hole-position"];
      if (config["cable-hole-position"]) result.payload.config.cableHolePosition = cableHolePosition === 'none' ? undefined : cableHolePosition;
      const skirtingCutout = this.buildSkirtingCutout(config)
      if (skirtingCutout) result.payload.config.skirtingCutout = skirtingCutout

      return result
    },

    // @param {Object} config - Configuration object
    // Carcasses payload: same baseline as bookcases, but without shelves
    // and with fixed leg code "kitchen" when base type is legs.
    buildCarcassesPayload(config) {
      let color = config["color"]
      if (config["materialRange"] && (config["materialRange"].startsWith('/') || config["materialRange"].startsWith('http'))) {
        color = config["materialRange"]
      }
      if (config["materialRange"] === 'luxe') {
        color = config["luxeColor"]
      }

      const allowedBaseTypes = new Set(['plinth', 'legs', 'floor', 'wall'])
      let baseTypeType = (config["baseType-type"] || '').toString().trim().toLowerCase()
      if (config["mountType"] === 'wall') baseTypeType = 'wall'
      if (!allowedBaseTypes.has(baseTypeType)) baseTypeType = 'floor'
      const baseType = { type: baseTypeType }

      const result = {
        source: 'cabinet-configurator-app',
        payload: {
          type: 'setStartConfig',
          doorToggleButtonVisible: false, // specific for carcasses (no doors)
          config: {
            color: color,
            mountType: config["mountType"],
            baseType,
            columns: this.buildColumnsPayload(config, 'carcasses'),
            countertop: this.buildCountertopPayload(config),
            topPanelEnabled: this.buildTopPanelEnabled(config),
            dimensions: {
              width: Number(config["dimensions-width"]),
              height: Number(config["dimensions-height"]),
              depth: Number(config["dimensions-depth"]),
            },
            doorHangType: 'standard',
            faceFrameCoversVerticalDividers: true,
          }
        }
      }

      if (result.payload.config.baseType.type === 'legs') {
        const allowedLegColors = new Set([
          'black', 'raw_steel', 'clear_coat', 'zinc', 'white', 'yellow', 'red',
          'pastel_green', 'duck_egg', 'orange', 'copper', 'gold', 'satin_brass',
          'satin_nickel', 'antique_brass', 'bright_chrome', 'stainless_steel', 'vintage_gold',
        ])
        const rawLegColor = (config["baseType-color"] == null ? '' : String(config["baseType-color"]).trim().toLowerCase())
        result.payload.config.baseType.legCode = 'kitchen'
        // 3D schema accepts only enum leg colors (not material URLs/hex values).
        result.payload.config.baseType.color = allowedLegColors.has(rawLegColor) ? rawLegColor : 'black'
      } else if (result.payload.config.baseType.type === 'plinth') {
        result.payload.config.baseType.height = Number(config["baseType-plinth-height"])
      }

      if (config['edge-color-yes-no'] === 'no') {
        result.payload.config.edgeColor = undefined
      } else if (config['edge-color']) {
        result.payload.config.edgeColor = config["edge-color"]
      }

      // No shelves for carcasses.

      if (config["countertopColor"] === "null" || !config["countertopColor"]) result.payload.config.countertopColor = undefined
      else result.payload.config.countertopColor = config["countertopColor"]

      if (config["faceFrame"]) result.payload.config.withFaceFrame = config["faceFrame"]?.includes('true')
      if (config['columnsCount']) result.payload.config.columnsCount = Number(config['columnsCount'])
      const cableHolePosition = config["cable-hole-position"]
      if (config["cable-hole-position"]) result.payload.config.cableHolePosition = cableHolePosition === 'none' ? undefined : cableHolePosition
      const skirtingCutout = this.buildSkirtingCutout(config)
      if (skirtingCutout) result.payload.config.skirtingCutout = skirtingCutout

      result.payload.config.bottomShelfFit = 'inset'

      return result
    },

    parseColumnTemplateValue(value) {
      if (!value) return { code: undefined, meta: undefined }
      const raw = String(value).trim()
      const [code, meta] = raw.split(':')
      return {
        code: code ? code.trim() : undefined,
        meta: meta ? meta.trim() : undefined
      }
    },

    _coerceNumber(value) {
      if (value === null || value === undefined || value === '') return undefined
      const num = Number(value)
      return Number.isFinite(num) ? num : undefined
    },

    _coerceBoolean(value) {
      if (value === null || value === undefined) return undefined
      if (typeof value === 'boolean') return value
      if (Array.isArray(value)) {
        const match = value.map(v => this._coerceBoolean(v)).find(v => v !== undefined)
        return match
      }
      const normalized = String(value).toLowerCase().trim()
      if (['true', '1', 'yes', 'on'].includes(normalized)) return true
      if (['false', '0', 'no', 'off', 'none', 'null'].includes(normalized)) return false
      return undefined
    },

    buildTopPanelEnabled(config = {}) {
      const explicit = this._coerceBoolean(
        config['top-panel-enabled'] ??
        config['topPanelEnabled'] ??
        config?.topPanelEnabled ??
        config?.topPanel?.isEnabled ??
        config?.topPanel?.enabled
      )
      return explicit !== undefined ? explicit : true
    },

    buildCountertopPayload(config = {}) {
      const explicitIsEnabled = this._coerceBoolean(
        config['countertop-enabled'] ??
        config?.countertop?.isEnabled ??
        config?.countertopEnabled ??
        config?.isCountertopEnabled
      )
      const doorHangType = (
        config['doors-type'] ??
        config['doorHangType'] ??
        config?.doorHangType
      )
      const isInsetDoorType = String(doorHangType || '').trim().toLowerCase() === 'inset'

      const overhangSource = config['countertop-overhang'] ?? config?.countertop?.overhang
      let overhang
      if (overhangSource) {
        if (typeof overhangSource === 'string') {
          overhang = {
            left: overhangSource?.includes('left'),
            right: overhangSource?.includes('right'),
            front: overhangSource?.includes('front'),
            back: overhangSource?.includes('back'),
          }
        } else if (Array.isArray(overhangSource)) {
          overhang = {
            left: overhangSource.includes('left'),
            right: overhangSource.includes('right'),
            front: overhangSource.includes('front'),
            back: overhangSource.includes('back'),
          }
        } else if (typeof overhangSource === 'object') {
          overhang = {
            left: !!overhangSource.left,
            right: !!overhangSource.right,
            front: !!overhangSource.front,
            back: !!overhangSource.back,
          }
        }
      }
      const hasOverhang = overhang ? Object.values(overhang).some(Boolean) : false
      if (overhang && !hasOverhang) {
        overhang = undefined
      }

      const isEnabled = explicitIsEnabled !== undefined
        ? explicitIsEnabled
        : true
      if (!isEnabled) {
        return { isEnabled: false }
      }

      const shouldAdd = !isInsetDoorType
      const payload = { isEnabled: true, shouldAdd }
      if (overhang) payload.overhang = overhang
      return payload
    },

    buildSkirtingCutout(config = {}) {
      const toggle = this._coerceBoolean(config['cut-out'])
      const limits = config?.__skirtingCutoutLimits || {}
      const defaultMin = 5
      const clampToRange = (value, range) => {
        const min = Number(range?.min)
        const max = Number(range?.max)
        let v = Number.isFinite(value) ? value : (Number.isFinite(min) ? min : defaultMin)
        if (Number.isFinite(min) && v < min) v = min
        if (Number.isFinite(max) && v > max) v = max
        if (!Number.isFinite(min) && !Number.isFinite(max) && v < defaultMin) v = defaultMin
        return v
      }

      let height = this._coerceNumber(
        config['cut-out-height'] ??
        config?.skirtingCutout?.height
      )
      let depth = this._coerceNumber(
        config['cut-out-depth'] ??
        config?.skirtingCutout?.depth
      )
      height = clampToRange(height, limits?.height)
      depth = clampToRange(depth, limits?.depth)

      const flag = (toggle !== undefined) ? toggle : false

      // If explicitly disabled, still send dimensions (validator requires fields)
      if (flag === false) {
        return { isEnabled: false, shouldAdd: false, height, depth }
      }

      const payload = { height, depth }
      payload.isEnabled = flag
      payload.shouldAdd = flag
      return payload
    },

    buildColumnDoorsConfig(config = {}, index = 1) {
      const direction = config[`doors-openingDirection-${index}`] ?? config[`doors-simpleOpeningDirection-${index}`]
      const hingesCount = 0
      
      const rawTopOffset = this._coerceNumber(
        config[`top-hinge-offset-${index}`] ??
        config['top-hinge-offset'] ??
        config['topHingeOffset']
      )
      const rawBottomOffset = this._coerceNumber(
        config[`bottom-hinge-offset-${index}`] ??
        config['bottom-hinge-offset'] ??
        config['bottomHingeOffset']
      )
      let adjustedTop = Number.isFinite(rawTopOffset) ? rawTopOffset : 30
      let adjustedBottom = Number.isFinite(rawBottomOffset) ? rawBottomOffset : 30
      if (adjustedTop <= 0) adjustedTop = 30
      if (adjustedBottom <= 0) adjustedBottom = 30

      const payload = {}
      if (direction) payload.direction = direction
      if (hingesCount != null) payload.hingesCount = hingesCount
      payload.topHingeOffset = adjustedTop
      payload.bottomHingeOffset = adjustedBottom

      return Object.keys(payload).length > 0 ? payload : undefined
    },

    buildColumnsPayload(config = {}, configuratorType = 'classic') {
      const columns = []
      const isWallUnit = configuratorType === 'wall-unit'
      const declaredCount = Number(config['columnsCount'])
      const maxDeclared = Number.isFinite(declaredCount) ? Math.max(0, Math.min(4, declaredCount)) : 0
      const templateIndices = Object.keys(config || {}).reduce((max, key) => {
        if (!key.startsWith('columnTemplate-')) return max
        const idx = parseInt(key.replace(/^\D+/, ''), 10)
        if (!Number.isFinite(idx)) return max
        return Math.max(max, idx)
      }, 0)
      const loopMax = maxDeclared > 0 ? maxDeclared : templateIndices

      const readNumber = (...keys) => {
        for (const key of keys) {
          if (key == null) continue
          const value = config[key]
          const num = this._coerceNumber(value)
          if (num !== undefined) return num
        }
        return undefined
      }

      const shouldIncludeDoorsConfig = (code, column) => {
        if (code === 'T6' || code === 'T7') return true
        return !!column?.hasDoor
      }

      const getShelvesCountForColumn = (index) => {
        const explicit = readNumber(`columnShelvesCount-${index}`)
        if (explicit !== undefined) return explicit
        const topLevel = config['shelvesCount']
        if (Array.isArray(topLevel)) {
          const val = this._coerceNumber(topLevel[index - 1])
          if (val !== undefined) return val
        }
        const fallback = this._coerceNumber(topLevel)
        return fallback
      }

      const getDrawersCountForColumn = (index) => {
        const explicit = readNumber(`columnDrawersCount-${index}`)
        if (explicit !== undefined) return explicit
        const fallback = this._coerceNumber(config['drawersCount'])
        return fallback
      }

      for (let i = 1; i <= loopMax; i++) {
        const templateRaw = config[`columnTemplate-${i}`]
        if (!templateRaw || templateRaw === 'null' || String(templateRaw).trim() === '') continue
        const { code, meta } = this.parseColumnTemplateValue(templateRaw)
        if (!code) continue
        const column = { code }
        if (meta && code === 'T4') {
          const lower = meta.toLowerCase()
          if (lower === 'hasdoor' || lower === 'door') column.hasDoor = true
          else if (lower === 'nodoor') column.hasDoor = false
        }

        if (code === 'T1') {
          if (!isWallUnit) {
            const drawersCount = getDrawersCountForColumn(i)
            if (drawersCount !== undefined) column.drawersCount = drawersCount
          }
        } else if (code === 'T2') {
          if (configuratorType !== 'carcasses') {
            const shelvesCount = getShelvesCountForColumn(i)
            column.shelvesCount = shelvesCount !== undefined ? shelvesCount : 0
          }
          const dividerHeight = readNumber(`columnDividerHeight-${i}`)
          if (dividerHeight !== undefined) column.horizontalDividerHeight = dividerHeight
          if (!isWallUnit) {
            const drawersCount = getDrawersCountForColumn(i)
            if (drawersCount !== undefined) column.drawersCount = drawersCount
          }
        } else if (code === 'T3') {
          if (configuratorType !== 'carcasses') {
            const shelvesCount = getShelvesCountForColumn(i)
            column.shelvesCount = shelvesCount !== undefined ? shelvesCount : 0
          }
        } else if (code === 'T4') {
          if (configuratorType !== 'carcasses') {
            const shelvesCount = getShelvesCountForColumn(i)
            column.shelvesCount = shelvesCount !== undefined ? shelvesCount : 0
          }
        } else if (code === 'T5') {
          if (configuratorType !== 'carcasses') {
            const shelvesCount = getShelvesCountForColumn(i)
            column.shelvesCount = shelvesCount !== undefined ? shelvesCount : 0
          }
          const dividerHeight = readNumber(`columnDividerHeight-${i}`)
          if (dividerHeight !== undefined) column.horizontalDividerHeight = dividerHeight
          if (!isWallUnit) {
            const drawersCount = getDrawersCountForColumn(i)
            if (drawersCount !== undefined) column.drawersCount = drawersCount
          }
        } else if (code === 'T6' || code === 'T7') {
          if (configuratorType !== 'carcasses') {
            const shelvesCount = getShelvesCountForColumn(i)
            if (shelvesCount !== undefined) column.shelvesCount = shelvesCount
          }
          const dividerHeight = readNumber(`columnDividerHeight-${i}`)
          if (dividerHeight !== undefined) column.horizontalDividerHeight = dividerHeight
          if (!isWallUnit) {
            const drawersCount = getDrawersCountForColumn(i)
            if (drawersCount !== undefined) column.drawersCount = drawersCount
          }
        }

        if (shouldIncludeDoorsConfig(code, column)) {
          const doorsConfig = this.buildColumnDoorsConfig(config, i)
          if (doorsConfig) column.doorsConfig = doorsConfig
        }

        columns.push(column)
      }

      return columns
    },

    buildPayload(config) {
      const type = (global.Visualizer && typeof global.Visualizer.getConfigType === 'function') ? global.Visualizer.getConfigType() : 'classic'
      if (type === 'door') {
        return this.buildDoorPayload(config)
      } else if (type === 'bookcases') {
        return this.buildBookcasesPayload(config, type)
      } else if (type === 'carcasses') {
        return this.buildCarcassesPayload(config)
      } else if (type === 'wall-unit') {
        // Wall units use the same payload as classic, but with forced wall mounting
        return this.buildClassicPayload(config, type)
      }
      return this.buildClassicPayload(config, type)
    },

    post(iframe, config, currentTab, Validation, isConfiguratorLoaded) {
      if (!iframe) {
        console.log('not define frame to visualize')
        return
      }
      if (!Validation || !Validation.isConfigReady(config, isConfiguratorLoaded)) {
        const issues = Validation ? Validation.getConfigReadinessIssues(config, { includeIframeCheck: true, isConfiguratorLoaded }) : ['validation module missing']
        if (issues.includes('iframe not loaded')) return;
        console.log('config not ready yet, postponing postMessage. Missing/invalid:', issues)
        return
      }
      const data = Message.buildPayload(config)

      if (!data) return;
      const asJson = JSON.stringify(data)
      if (asJson === Message._last) return

      Message._last = asJson
      iframe.contentWindow.postMessage(data, '*')
    },

    postDoorsDisplayMode(iframe, mode) {
      if (!iframe) {
        console.log('not define frame to visualize')
        return
      }
      const data = {
        source: 'cabinet-configurator-app',
        payload: { type: 'setDoorsDisplayMode', mode }
      }
      iframe.contentWindow.postMessage(data, '*')
    }
  }

  global.Visualizer = global.Visualizer || {}
  global.Visualizer.Message = Message
})(window);
